import type {
  EstimateNewOrderRequestBody,
  EstimateIntercityOrderRequestBody,
} from '@ahm/api-wrappers-core';
import { toast } from '@ahm/react-ui';
import { closeModal, openContextModal } from '@mantine/modals';
import { usePathname, useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';
import { useCallback } from 'react';
import { ModalName } from '../..';
import { ROUTES } from '@/config/router';
import { useEstimateIntercityOrder } from '@/features/booking/api/intercity';
import { useEstimateOrder } from '@/features/booking/api/order/use-estimate-order';
import { useBookingRequestBodies } from '@/features/booking/hooks/use-booking-request';
import { EXCEEDED_DISCOUNT_LIMIT } from '@/lib/constants';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

/**
 * Hook to handle promotion estimate logic
 * Centralizes the estimate and apply promo code functionality
 */
export const usePromotionEstimate = () => {
  const pathname = usePathname();
  const router = useRouter();
  const tPromo = useTranslations('booking.promo');
  const { estimateRequestBody } = useBookingRequestBodies(pathname, null);

  const {
    selectedService,
    setErrorPromo,
    setShowToastPromo,
    setEstimatedData,
    setIsSuggestPromotion,
  } = useBookingStore((state) => state);

  const [, setUrlPromoIds] = useQueryState(
    'promo_code',
    parseAsArrayOf(parseAsString).withDefault([])
  );

  // Constants
  const INVALID_ESTIMATE_ERROR = 'Invalid estimate request body';

  const applyValidPromoCodes = useCallback(
    (discountDetails: any[]) => {
      const validPromoCodeIds = discountDetails
        .filter((item) => item.is_valid)
        .map((item) => item.promo_code_id);

      if (validPromoCodeIds.length > 0) {
        toast.success({
          title: tPromo('notify_promo_code_apply_success'),
          message: null,
          withIcon: true,
          withCloseButton: true,
          onClose: () => {
            setShowToastPromo(false);
          },
        });
        void setUrlPromoIds(validPromoCodeIds);
      } else {
        void setUrlPromoIds([]);
      }
    },
    [tPromo, setShowToastPromo, setUrlPromoIds]
  );

  const handleExceedDiscountLimit = useCallback((discountDetails: any[]) => {
    openContextModal({
      modal: ModalName.ExceedDiscountLimit,
      modalId: ModalName.ExceedDiscountLimit,
      innerProps: {
        discountDetails,
      },
      fullScreen: false,
      centered: true,
      withCloseButton: false,
      closeOnClickOutside: false,
      closeOnEscape: false,
    });
  }, []);

  const handleEstimateSuccess = useCallback(
    (res: any[]) => {
      if (res.length && res[0].error) {
        setErrorPromo(res[0].error.description ?? 'estimate promos error');
        setShowToastPromo(true);
        closeModal(ModalName.Promotion);
        toast.error({
          title: res[0].error.title,
          message: res[0].error.description,
          withIcon: true,
        });
        return;
      }

      const estData = selectedService
        ? res.find((item) => item.service_id === selectedService._id)
        : res[0];

      const discountDetails = estData.data?.discount_details || [];

      if (!discountDetails.length) {
        void setUrlPromoIds([]);
      }

      const hasPromoError = discountDetails?.some(
        (detail) => !detail.is_valid && detail.promo_code_id
      );

      const hasExceededDiscountLimit = discountDetails?.some(
        (item) => item.error_code === EXCEEDED_DISCOUNT_LIMIT
      );

      setEstimatedData(estData ?? null);

      if (!hasExceededDiscountLimit && discountDetails.length > 0) {
        applyValidPromoCodes(discountDetails);
      }

      if (hasPromoError && hasExceededDiscountLimit) {
        handleExceedDiscountLimit(discountDetails);
        return;
      }

      closeModal(ModalName.Promotion);
    },
    [
      selectedService,
      setErrorPromo,
      setShowToastPromo,
      setEstimatedData,
      setUrlPromoIds,
      applyValidPromoCodes,
      handleExceedDiscountLimit,
    ]
  );

  const handleEstimateError = useCallback((error: any) => {
    const responseError = error?.response?.data;
    console.error('estimate with promo code error', error);
    toast.error({
      title: responseError?.description || 'estimate promos error',
      message: responseError?.message,
      withIcon: true,
      withCloseButton: true,
    });
  }, []);

  const { mutate: estimateOrder } = useEstimateOrder({
    onSuccess: handleEstimateSuccess,
    onError: handleEstimateError,
  });

  const { mutate: estimateIntercityOrder } = useEstimateIntercityOrder({
    onSuccess: handleEstimateSuccess,
    onError: handleEstimateError,
  });

  /**
   * Call appropriate estimate API based on pathname
   * @param body - Request body for estimate
   */
  const callEstimateAPI = useCallback(
    (body: any) => {
      if (pathname === ROUTES.nationwide) {
        estimateIntercityOrder(
          body as unknown as EstimateIntercityOrderRequestBody
        );
      } else {
        estimateOrder(body as EstimateNewOrderRequestBody);
      }
    },
    [pathname, estimateIntercityOrder, estimateOrder]
  );

  /**
   * Apply promo codes with estimate
   * @param promoCodeIds - Array of promo code IDs to apply
   */
  const applyPromoCodesWithEstimate = useCallback(
    (promoCodeIds: string[]) => {
      setIsSuggestPromotion(false);

      if (!estimateRequestBody || !('path' in estimateRequestBody)) {
        console.error(INVALID_ESTIMATE_ERROR);
        return;
      }

      const body = {
        ...estimateRequestBody,
        promo_code_ids: promoCodeIds.length > 0 ? promoCodeIds : undefined,
        suggest_promotion: false,
      };

      callEstimateAPI(body);
    },
    [setIsSuggestPromotion, estimateRequestBody, callEstimateAPI]
  );

  /**
   * Separate estimate function for exceed discount limit modal
   * Apply only valid promo codes after user confirms
   */
  const applyValidPromoCodesFromExceedModal = useCallback(
    (discountDetails: any[]) => {
      setIsSuggestPromotion(false);

      if (!estimateRequestBody || !('path' in estimateRequestBody)) {
        console.error(INVALID_ESTIMATE_ERROR);
        return;
      }

      // Get only valid promo codes
      const validPromoCodeIds = discountDetails
        .filter((item) => item.is_valid)
        .map((item) => item.promo_code_id);

      const body = {
        ...estimateRequestBody,
        promo_code_ids:
          validPromoCodeIds.length > 0 ? validPromoCodeIds : undefined,
      };

      // Call appropriate estimate API using helper function
      callEstimateAPI(body);
    },
    [setIsSuggestPromotion, estimateRequestBody, callEstimateAPI]
  );

  /**
   * Apply a single promo code with estimate
   * @param promoCodeId - Single promo code ID to apply
   * @param existingPromoIds - Existing promo code IDs
   */
  const applySinglePromoCode = useCallback(
    (promoCodeId: string, existingPromoIds: string[] = []) => {
      const newPromoIds = [...existingPromoIds, promoCodeId];
      applyPromoCodesWithEstimate(newPromoIds);
    },
    [applyPromoCodesWithEstimate]
  );

  return {
    applyValidPromoCodes,
    handleExceedDiscountLimit,
    handleEstimateSuccess,
    applyPromoCodesWithEstimate,
    applySinglePromoCode,
    applyValidPromoCodesFromExceedModal,
    estimateOrder,
  };
};
